import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Check, X, Eye, EyeOff, HelpCircle, Save, Sparkles, Ban, RotateCcw, Stethoscope,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { API_URL } from '../config/api';
import { motion } from 'framer-motion';
import { Skeleton } from './ui/skeleton';
import { anima, aparecer } from './movimientoWhatsApp';
import PreguntasPropiasWhatsApp, { nuevoId } from './PreguntasPropiasWhatsApp';
import { cn } from '../lib/utils';

/**
 * Qué contesta el asistente: el formulario que le enseña.
 *
 * QUÉ RESUELVE
 * Cuando alguien le pregunta algo que no sabe, el bot dice "ese dato no lo
 * tengo cargado". Es lo correcto —un dato inventado manda a una persona hasta
 * el local confiando en algo falso— pero el negocio no tenía forma de
 * arreglarlo: el conocimiento vivía en un archivo dentro del repo del bot, así
 * que cargarlo era editar código.
 *
 * TRES BLOQUES, Y EL DE ARRIBA IMPORTA MÁS
 *
 * 1. Lo que ya preguntaron y el bot no supo. Son preguntas reales de clientes
 *    reales, con cuántas veces las hicieron. Antes se perdían.
 *
 * 2. El formulario, en dos columnas. Las preguntas que sabemos que se hacen en
 *    cualquier local. A una por fila la pantalla pedía scrollear seis veces
 *    para ver siete categorías, y lo que no se ve no se contesta.
 *
 * 3. Tus preguntas. Las de este negocio en particular, con el disparador y la
 *    respuesta que el negocio elija. Ahí va a parar "Contestar" del primer
 *    bloque: antes esa pregunta se pegaba como texto al final del campo libre y
 *    quedaba a mitad de camino entre una nota y una respuesta.
 *
 * CUATRO FORMAS DE CONTESTAR
 * Un campo de texto vacío con un título arriba es trabajo de redacción, y el
 * dueño de un local no se sienta a redactar. El sí/no se contesta con el pulgar
 * y su campo de al lado —el "u otro"— es para cuando la respuesta es "sí,
 * pero...". Lo que es una cantidad se carga como cantidad: "cuánto antes
 * conviene llegar" escrito a mano lo contesta cada negocio distinto y la mitad
 * pone "depende"; con dos casilleros se contesta tecleando, y la frase la arma
 * el catálogo del backend.
 *
 * NADA ES OBLIGATORIO, Y ALGUNAS NO APLICAN
 * Se contesta lo que se quiere, cuando se quiere. Y lo que no tiene nada que
 * ver con este negocio —las mascotas en un consultorio, la sala de espera en
 * un delivery— se marca "no aplica": no se le escribe nada al bot y sale del
 * contador, que si no se queda para siempre en "12 de 40" y deja de significar
 * algo.
 */

/** Un sí / no / y-además, en una fila. */
function Ternario({ valor, onCambio }) {
  const base = 'inline-flex items-center gap-1 rounded-lg border px-2.5 py-1 '
    + 'text-[0.78rem] font-semibold transition-colors';
  return (
    <div className="flex shrink-0 gap-1.5">
      <button
        type="button"
        onClick={() => onCambio(valor === 'si' ? null : 'si')}
        aria-pressed={valor === 'si'}
        className={cn(base, valor === 'si'
          ? 'border-emerald-600 bg-emerald-600 text-white'
          : 'border-slate-300 bg-white text-slate-600 hover:bg-slate-50')}
      >
        <Check className="h-3.5 w-3.5" /> Sí
      </button>
      <button
        type="button"
        onClick={() => onCambio(valor === 'no' ? null : 'no')}
        aria-pressed={valor === 'no'}
        className={cn(base, valor === 'no'
          ? 'border-slate-700 bg-slate-700 text-white'
          : 'border-slate-300 bg-white text-slate-600 hover:bg-slate-50')}
      >
        <X className="h-3.5 w-3.5" /> No
      </button>
    </div>
  );
}

/**
 * Una cantidad, con su rango opcional y su unidad.
 *
 * El segundo casillero está siempre a la vista y vacío: es el "cinco o diez
 * minutos" con el que se contesta de verdad, y escondido detrás de un botón
 * nadie lo encuentra. Vacío no molesta —la frase sale igual con un número
 * solo— y lleno cambia "10 minutos" por "entre 5 y 10 minutos".
 */
function CampoNumerico({ config, desde, hasta, onCambio }) {
  const casilla = 'w-16 rounded-lg border border-slate-300 px-2 py-2 text-center '
    + 'text-[0.85rem] text-slate-800 outline-none placeholder:text-slate-300 focus:border-slate-400';
  /**
   * Lo que se teclea, acotado a lo que el backend acepta.
   *
   * Se recorta acá y no allá porque `normalizarRespuestas` descarta el número
   * fuera de rango entero: quien escribe 5000 minutos vería el 5000 en pantalla,
   * guardaría, y la respuesta desaparecería sin que nada lo explique.
   */
  const numero = (texto) => {
    if (texto === '') return null;
    const n = Number(texto);
    if (!Number.isFinite(n)) return undefined;   // undefined = tecla que no es número
    return Math.min(Math.max(Math.round(n), 0), config.maximo);
  };
  return (
    <div className="flex shrink-0 items-center gap-1.5">
      <input
        type="number"
        inputMode="numeric"
        min={0}
        max={config.maximo}
        value={desde ?? ''}
        onChange={(e) => {
          const n = numero(e.target.value);
          if (n !== undefined) onCambio({ desde: n });
        }}
        placeholder="5"
        className={casilla}
        aria-label="Desde"
      />
      <span className="text-[0.8rem] text-slate-400">a</span>
      <input
        type="number"
        inputMode="numeric"
        min={0}
        max={config.maximo}
        value={hasta ?? ''}
        onChange={(e) => {
          const n = numero(e.target.value);
          if (n !== undefined) onCambio({ hasta: n });
        }}
        placeholder="10"
        className={casilla}
        aria-label="Hasta (opcional)"
      />
      <span className="whitespace-nowrap text-[0.82rem] font-medium text-slate-500">
        {config.unidad}
      </span>
    </div>
  );
}

/** La configuración numérica de una pregunta, sea del tipo que sea. */
const configNumerica = (p) => (p.tipo === 'numero' ? p : (p.tipo === 'si_no' ? p.numeroSi : null));

const QueContestaWhatsApp = () => {
  const { currentUser } = useAuth();
  const [catalogo, setCatalogo] = useState([]);
  const [respuestas, setRespuestas] = useState({});
  const [propias, setPropias] = useState({ categorias: [], preguntas: [] });
  const [obrasSociales, setObrasSociales] = useState([]);
  const [limites, setLimites] = useState(null);
  const [sinResponder, setSinResponder] = useState([]);
  const [foco, setFoco] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [guardado, setGuardado] = useState(false);
  const [error, setError] = useState('');
  const [vistaPrevia, setVistaPrevia] = useState('');
  const [mostrarPrevia, setMostrarPrevia] = useState(false);
  // Lo último que se guardó, para saber si hay algo sin guardar. Comparar
  // contra esto y no contra lo que vino del servidor: si no, apenas se guarda
  // el cartel de "sin guardar" tarda un refresco en irse.
  const ultimoGuardado = useRef('{}');

  const pedir = useCallback(async (ruta, opciones = {}) => {
    const token = await currentUser.getIdToken();
    return fetch(`${API_URL}${ruta}`, {
      ...opciones,
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...(opciones.headers || {}),
      },
    });
  }, [currentUser]);

  const traer = useCallback(async () => {
    try {
      const r = await pedir('/api/whatsapp/conocimiento');
      if (!r.ok) throw new Error('No se pudo leer');
      const data = await r.json();
      const suyas = data.propias || { categorias: [], preguntas: [] };
      setCatalogo(data.catalogo || []);
      setRespuestas(data.respuestas || {});
      setPropias(suyas);
      setObrasSociales(data.obrasSociales || []);
      setLimites(data.limites || null);
      setSinResponder(data.sinResponder || []);
      ultimoGuardado.current = JSON.stringify({ respuestas: data.respuestas || {}, propias: suyas });
      setError('');
    } catch (e) {
      setError('No pude traer el formulario. Probá recargar.');
    } finally {
      setCargando(false);
    }
  }, [pedir]);

  useEffect(() => { traer(); }, [traer]);

  const cambiar = (clave, cambio) => {
    setGuardado(false);
    setRespuestas((previas) => {
      const actual = previas[clave] || { valor: null, detalle: '', desde: null, hasta: null };
      const nueva = { ...actual, ...cambio, noAplica: false };
      // Una respuesta que se vació deja de ser una respuesta. Si quedara, el
      // progreso la contaría como contestada y el negocio creería que le
      // enseñó algo al bot que el bot no sabe.
      const vacia = !nueva.valor
        && !(nueva.detalle || '').trim()
        && (nueva.desde === null || nueva.desde === undefined);
      if (vacia) {
        const { [clave]: _, ...resto } = previas;
        return resto;
      }
      return { ...previas, [clave]: nueva };
    });
  };

  /** "Esto en mi negocio no existe": ni se contesta ni queda pendiente. */
  const marcarNoAplica = (clave) => {
    setGuardado(false);
    setRespuestas((previas) => ({
      ...previas,
      [clave]: { valor: null, detalle: '', desde: null, hasta: null, noAplica: true },
    }));
  };

  const volverAActivar = (clave) => {
    setGuardado(false);
    setRespuestas(({ [clave]: _, ...resto }) => resto);
  };

  const cambiarPropias = (nuevas) => {
    setGuardado(false);
    setPropias(nuevas);
  };

  const guardar = async () => {
    setGuardando(true);
    setError('');
    try {
      const r = await pedir('/api/whatsapp/conocimiento', {
        method: 'PUT',
        body: JSON.stringify({ respuestas, propias }),
      });
      if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        throw new Error(data.error || 'No se pudo guardar');
      }
      const data = await r.json();
      setVistaPrevia(data.vistaPrevia || '');
      ultimoGuardado.current = JSON.stringify({ respuestas, propias });
      setGuardado(true);
    } catch (e) {
      setError(e.message);
    } finally {
      setGuardando(false);
    }
  };

  const descartar = async (clave) => {
    setSinResponder((previas) => previas.filter((p) => p.clave !== clave));
    await pedir('/api/whatsapp/conocimiento/resolver', {
      method: 'POST', body: JSON.stringify({ clave }),
    }).catch(() => {});
  };

  /**
   * Convierte una pregunta real en una pregunta propia, ya disparada por el
   * texto exacto que escribió el cliente.
   *
   * Antes esto pegaba la pregunta al final del campo libre y dejaba al negocio
   * escribiendo un párrafo: quedaba a mitad de camino entre una nota y una
   * respuesta, y el buscador devolvía las cinco juntas. Como pregunta propia,
   * el disparador ya es la forma exacta en que la gente lo escribe —que es lo
   * más difícil de adivinar— y lo único que falta es contestar.
   */
  const responderSuelta = (pregunta) => {
    if (limites && (propias.preguntas || []).length >= limites.preguntas) {
      setError(`Llegaste a ${limites.preguntas} preguntas propias, que es el máximo.`);
      return;
    }
    const id = nuevoId('p');
    cambiarPropias({
      ...propias,
      preguntas: [
        ...(propias.preguntas || []),
        { id, categoriaId: null, disparadores: [pregunta.texto], respuesta: '' },
      ],
    });
    setFoco(id);
    descartar(pregunta.clave);
  };

  const olvidarFoco = useCallback(() => setFoco(null), []);

  const todas = Object.values(respuestas);
  const noAplican = todas.filter((r) => r.noAplica).length;
  const contestadas = todas.length - noAplican;
  const total = catalogo.reduce((n, g) => n + g.preguntas.length, 0) - noAplican;
  const sinGuardar = JSON.stringify({ respuestas, propias }) !== ultimoGuardado.current;

  if (cargando) {
    return (
      <div className="space-y-4 py-2">
        <Skeleton className="h-5 w-64" />
        <div className="grid gap-4 lg:grid-cols-2">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="space-y-2 rounded-xl border border-slate-200 p-4">
              <Skeleton className="h-4 w-40" />
              <Skeleton className="h-9 w-full" />
              <Skeleton className="h-9 w-full" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    // Entra en vez de aparecer: el esqueleto se cambiaba por el contenido de
    // una sola vez y el salto se notaba más que la espera.
    <motion.div {...anima} {...aparecer(8)} className="flex flex-col gap-4 pb-24">
      <header>
        <h2 className="m-0 text-lg font-bold text-slate-900">Qué contesta</h2>
        <p className="m-0 mt-1 max-w-[70ch] text-sm leading-snug text-slate-500">
          Lo que cargues acá lo puede contestar el asistente. Lo que no,
          responde que no lo tiene cargado en vez de inventarlo. Nada es
          obligatorio: contestá lo que quieras, cuando quieras.
        </p>
        <p className="m-0 mt-2 text-[0.8rem] text-slate-400">
          Tus servicios, precios, horarios y obras sociales ya los sabe: salen
          de tu panel. Acá va lo que no está en ningún lado.
        </p>
      </header>

      {error && (
        <p className="m-0 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </p>
      )}

      {/* ---- Lo que ya preguntaron ----
          Arriba de todo a propósito: una pregunta que alguien hizo tres veces
          esta semana vale más que la número catorce de una lista que
          escribimos nosotros. */}
      {sinResponder.length > 0 && (
        <section className="rounded-xl border border-amber-200 bg-amber-50 p-4">
          <h3 className="m-0 flex items-center gap-2 text-[0.95rem] font-bold text-amber-900">
            <Sparkles className="h-4 w-4" />
            Te preguntaron esto y el asistente no supo
          </h3>
          <p className="m-0 mt-1 text-[0.8rem] leading-snug text-amber-800">
            Son preguntas reales de gente que te escribió. "Contestar" te la baja
            a <strong>Tus preguntas</strong> con el texto que usaron, y ahí
            escribís la respuesta una sola vez.
          </p>
          <ul className="m-0 mt-3 grid list-none gap-1.5 p-0 sm:grid-cols-2">
            {sinResponder.map((p) => (
              <li
                key={p.clave}
                className="flex flex-wrap items-center gap-2 rounded-lg bg-white px-3 py-2"
              >
                <span className="min-w-0 flex-1 text-[0.85rem] text-slate-800">
                  {p.texto}
                </span>
                {p.veces > 1 && (
                  <span className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-[0.7rem] font-bold text-amber-800">
                    {p.veces} veces
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => responderSuelta(p)}
                  className="shrink-0 rounded-lg border border-emerald-300 bg-white px-2.5 py-1 text-[0.78rem] font-semibold text-emerald-700 hover:bg-emerald-50"
                >
                  Contestar
                </button>
                <button
                  type="button"
                  onClick={() => descartar(p.clave)}
                  className="shrink-0 rounded-lg px-2 py-1 text-[0.78rem] text-slate-400 hover:text-slate-600"
                  title="No la muestres más"
                >
                  Descartar
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* ---- El formulario ----
          Dos categorías por fila desde 1024px. `items-start` para que una
          categoría corta no se estire hasta el alto de la de al lado. */}
      <div className="grid items-start gap-4 lg:grid-cols-2">
        {catalogo.map((grupo) => (
          <section key={grupo.seccion} className="rounded-xl border border-slate-200 bg-white p-4">
            <h3 className="m-0 text-[0.95rem] font-bold text-slate-900">{grupo.titulo}</h3>
            {grupo.ayuda && (
              <p className="m-0 mt-0.5 text-[0.78rem] text-slate-400">{grupo.ayuda}</p>
            )}

            <div className="mt-3 space-y-3">
              {grupo.preguntas.map((p) => {
                const r = respuestas[p.clave] || { valor: null, detalle: '', desde: null, hasta: null };
                const config = configNumerica(p);
                // El número de un sí/no sólo tiene sentido cuando la respuesta
                // es que sí: un porcentaje al lado de "no hacemos descuento" no
                // se puede decir.
                const conNumero = config && (p.tipo === 'numero' || r.valor === 'si');

                if (r.noAplica) {
                  return (
                    <div
                      key={p.clave}
                      className="flex flex-wrap items-center gap-2 rounded-lg bg-slate-50 px-2.5 py-1.5"
                    >
                      <span className="min-w-0 flex-1 text-[0.82rem] text-slate-400">
                        {p.texto}
                      </span>
                      <span className="shrink-0 rounded-full bg-slate-200 px-2 py-0.5 text-[0.7rem] font-semibold text-slate-500">
                        No aplica
                      </span>
                      <button
                        type="button"
                        onClick={() => volverAActivar(p.clave)}
                        className="inline-flex shrink-0 items-center gap-1 rounded-lg px-1.5 py-0.5 text-[0.75rem] font-semibold text-slate-500 hover:bg-white hover:text-slate-700"
                      >
                        <RotateCcw className="h-3 w-3" /> Activar
                      </button>
                    </div>
                  );
                }

                return (
                  <div key={p.clave} className="flex flex-wrap items-start gap-2">
                    <div className="flex w-full items-start gap-2">
                      <label
                        htmlFor={p.clave === 'libre' ? 'campo-libre' : `campo-${p.clave}`}
                        className="min-w-0 flex-1 text-[0.85rem] font-medium text-slate-700"
                      >
                        {p.texto}
                      </label>
                      {/* No aplica en TODAS: una peluquería no tiene nada que
                          decir sobre obras sociales y un consultorio nada sobre
                          mascotas, y decidir por ellos cuáles se pueden apagar
                          es adivinar. */}
                      <button
                        type="button"
                        onClick={() => marcarNoAplica(p.clave)}
                        title="Esta pregunta no tiene que ver con mi negocio"
                        className="inline-flex shrink-0 items-center gap-1 rounded-lg px-1.5 py-0.5 text-[0.72rem] text-slate-300 transition-colors hover:bg-slate-50 hover:text-slate-500"
                      >
                        <Ban className="h-3 w-3" /> No aplica
                      </button>
                    </div>

                    {p.tipo === 'si_no' && (
                      <Ternario
                        valor={r.valor}
                        onCambio={(valor) => cambiar(p.clave, { valor })}
                      />
                    )}

                    {conNumero && (
                      <CampoNumerico
                        config={config}
                        desde={r.desde}
                        hasta={r.hasta}
                        onCambio={(cambio) => cambiar(p.clave, cambio)}
                      />
                    )}

                    {p.tipo === 'largo' ? (
                      <textarea
                        id="campo-libre"
                        rows={4}
                        value={r.detalle || ''}
                        onChange={(e) => cambiar(p.clave, { detalle: e.target.value })}
                        placeholder={p.ejemplo}
                        className="min-w-0 flex-1 resize-y rounded-lg border border-slate-300 px-3 py-2 text-[0.85rem] text-slate-800 outline-none placeholder:text-slate-300 focus:border-slate-400"
                      />
                    ) : (
                      <input
                        id={`campo-${p.clave}`}
                        type="text"
                        value={r.detalle || ''}
                        onChange={(e) => cambiar(p.clave, { detalle: e.target.value })}
                        /* En un sí/no y en un número el campo es el "u otro":
                           el matiz que hace que la respuesta sea verdad. "Sí,
                           pero solo perros chicos" no es lo mismo que "sí", y
                           "los sábados se llenan antes" no entra en un número. */
                        placeholder={p.tipo === 'texto' ? p.ejemplo : 'Aclaración (opcional)'}
                        className="min-w-0 flex-1 basis-40 rounded-lg border border-slate-300 px-3 py-2 text-[0.85rem] text-slate-800 outline-none placeholder:text-slate-300 focus:border-slate-400"
                      />
                    )}
                  </div>
                );
              })}

              {/* ---- Las obras sociales no se preguntan ----
                  Se cargan una vez en el panel y las usan la reserva, la página
                  pública y el asistente. Repetirlas acá sería pedirle al
                  negocio que mantenga lo mismo en dos lugares, y eso garantiza
                  que un día se contradigan. */}
              {grupo.seccion === 'pagos' && (
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                  <p className="m-0 flex items-center gap-1.5 text-[0.85rem] font-medium text-slate-700">
                    <Stethoscope className="h-3.5 w-3.5 text-slate-400" />
                    ¿Trabajan con obras sociales?
                  </p>
                  {obrasSociales.length > 0 ? (
                    <>
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {obrasSociales.map((nombre) => (
                          <span
                            key={nombre}
                            className="rounded-full bg-white px-2.5 py-1 text-[0.78rem] text-slate-700"
                          >
                            {nombre}
                          </span>
                        ))}
                      </div>
                      <p className="m-0 mt-2 text-[0.75rem] text-slate-400">
                        Esta la contesta solo, con las que tildaste en Mi negocio
                        → Obras sociales. Se cambia ahí y el asistente se entera.
                      </p>
                    </>
                  ) : (
                    /* Sin ninguna tildada el aviso se queda en una línea: la
                       mayoría de los rubros no trabaja con obras sociales y no
                       hay por qué explicarle a una peluquería dónde cargarlas. */
                    <p className="m-0 mt-1 text-[0.75rem] text-slate-400">
                      Si trabajás con alguna, se tilda en Mi negocio → Obras
                      sociales y el asistente la toma de ahí.
                    </p>
                  )}
                </div>
              )}
            </div>
          </section>
        ))}
      </div>

      {/* ---- Tus preguntas ---- */}
      {limites && (
        <PreguntasPropiasWhatsApp
          propias={propias}
          limites={limites}
          foco={foco}
          onCambio={cambiarPropias}
          onFocoTomado={olvidarFoco}
        />
      )}

      {/* ---- Vista previa ----
          Es el texto EXACTO que va a leer el asistente, generado por el mismo
          código que lo genera de verdad. Una vista previa que se parece a lo
          que va a pasar sirve para menos que no tener ninguna. */}
      {vistaPrevia && (
        <section className="rounded-xl border border-slate-200 bg-slate-50 p-4">
          <button
            type="button"
            onClick={() => setMostrarPrevia((v) => !v)}
            className="flex items-center gap-2 text-[0.85rem] font-semibold text-slate-700"
          >
            {mostrarPrevia ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            {mostrarPrevia ? 'Ocultar' : 'Ver'} lo que va a saber el asistente
          </button>
          {mostrarPrevia && (
            <pre className="mt-3 max-h-80 overflow-auto whitespace-pre-wrap break-words rounded-lg bg-white p-3 text-[0.78rem] leading-relaxed text-slate-600">
              {vistaPrevia}
            </pre>
          )}
        </section>
      )}

      {/* ---- La barra de guardar ----
          Pegada abajo: el formulario es largo y un botón al final obliga a
          bajar hasta el fondo para guardar un cambio hecho arriba. */}
      <div className="sticky bottom-0 -mx-1 flex flex-wrap items-center gap-3 border-t border-slate-200 bg-white/95 px-1 py-3 backdrop-blur">
        <span className="flex items-center gap-1.5 text-[0.82rem] text-slate-500">
          <HelpCircle className="h-3.5 w-3.5 text-slate-400" />
          {contestadas} de {total} contestadas
          {(propias.preguntas || []).length > 0 && (
            <> + {propias.preguntas.length} tuyas</>
          )}
        </span>

        {sinGuardar && (
          <span className="text-[0.8rem] font-semibold text-amber-700">
            Hay cambios sin guardar
          </span>
        )}
        {guardado && !sinGuardar && (
          <span className="text-[0.8rem] font-semibold text-emerald-700">
            Guardado. El asistente ya lo sabe.
          </span>
        )}

        <button
          type="button"
          onClick={guardar}
          disabled={guardando || !sinGuardar}
          className={cn(
            'ml-auto inline-flex shrink-0 items-center gap-1.5 rounded-lg px-4 py-2',
            'text-[0.85rem] font-semibold text-white transition-colors',
            'bg-emerald-600 hover:bg-emerald-700',
            'disabled:bg-slate-200 disabled:text-slate-400',
          )}
        >
          <Save className="h-3.5 w-3.5" />
          {guardando ? 'Guardando…' : 'Guardar'}
        </button>
      </div>
    </motion.div>
  );
};

export default QueContestaWhatsApp;
